import { isDefined } from '@sgme/fp';
import { useContext } from 'react';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { FixingContext, useValidateFixing } from '@/App/tiles/orders/FixingOrder/FixingContext.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectFixingCanModifyMarginInBps,
  selectFixingCommercialMargin,
  selectFixingErrors,
} from '@/store/orders/fixing/selectors.ts';
import { useDebounce } from '@/utils/hooks/debounce.ts';

export const FixingCommercialMargin = () => {
  const { tileId, setIsModifyingField } = useContext(FixingContext);
  const track = useTrackEvent();

  const commercialMargin = useAppSelector((state) => selectFixingCommercialMargin(state, tileId));
  const canModifyMarginInBps = useAppSelector((state) => selectFixingCanModifyMarginInBps(state, tileId));

  const errors = useAppSelector((state) => selectFixingErrors(state, tileId));
  const commercialMarginError = errors?.commercialMargin;

  const validateFixing = useValidateFixing();

  const handleCommercialMarginChange = useDebounce((newValue: number | undefined) => {
    track('Order action', 'Commercial margin changed');
    validateFixing({
      commercialMargin: newValue,
    });
  });

  return (
    <OrderRow labelId="order.fields.commercialMargin.label">
      <div className="input-group input-group-sm flex-nowrap">
        <OmsFieldErrorWrapper errors={commercialMarginError}>
          {canModifyMarginInBps ? (
            <>
              <SgNumberInput
                valuePosition="start"
                maxDecimals={5}
                value={commercialMargin}
                data-e2e="commercial-margin"
                onChange={handleCommercialMarginChange}
                onFocus={() => setIsModifyingField(true)}
                onBlur={() => setIsModifyingField(false)}
                size="sm"
                selectAllOnFocus
                state={isDefined(commercialMarginError) ? 'invalid' : undefined}
              />
              <span className="input-group-text">Bps</span>
            </>
          ) : (
            <span data-e2e="commercial-margin-text">{commercialMargin} Bps</span>
          )}
        </OmsFieldErrorWrapper>
      </div>
    </OrderRow>
  );
};
